import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { omit } from 'lodash-es';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import z from 'zod';
import { useDialog } from '@zep/dialog';
import { ExclamationmarkSmallTriangleFillIcon } from '@zep/icons';
import { Button, Checkbox, Input, TextField } from '@zep/ui';
import { getChannelTalkSupportArticleUrl } from '@zep/utils';
import { useChangeSpaceOwnerMutation } from '../../services';
import { emailRegex } from '../../utils';
import S from './SpaceTransfer.module.scss';
export const SpaceTransferPageView = () => {
  const {
    locale = 'ko'
  } = useRouter();
  const {
    t
  } = useTranslation(['manage']);
  const dialog = useDialog();
  const {
    watch,
    formState: {
      errors
    },
    register,
    handleSubmit,
    reset
  } = useForm<SpaceTransferSchemaType>({
    resolver: zodResolver(SpaceTransferSchema(t)),
    defaultValues: {
      targetPlayerEmail: '',
      spaceUri: '',
      isChecked: false
    },
    reValidateMode: 'onSubmit'
  });
  const isChecked = watch('isChecked');
  const changeSpaceOwnerMutation = useChangeSpaceOwnerMutation();
  const onSubmit = async (data: SpaceTransferSchemaType) => {
    try {
      await changeSpaceOwnerMutation.mutateAsync(omit(data, ['isChecked']));
      await dialog.alert({
        title: t('manage:space_transfer_success')
      });
      reset();
    } catch (error) {
      let message = '';
      if (error instanceof AxiosError && error.response?.status === 401) {
        message = t('manage:space_transfer_error_not_space_owner');
      } else {
        // @ts-ignore
        switch (error.status) {
          case -4: // NOT_FOUND_PLAYER
          case -182:
            // TARGET_PLAYER_IS_NOT_EDU_ACCOUNT
            message = t('manage:space_transfer_error_not_edu_account');
            break;
          case -48:
            // NOT_FOUND_SPACE
            message = t('manage:space_transfer_error_not_found_space');
            break;
          case -7: // PLEASE_LOGIN
          case -71:
            // NO_PERMISSION_TO_CHANGE_SPACE_MEMBER_ROLE
            message = t('manage:space_transfer_error_not_space_owner');
            break;
          case -183:
            // SPACE_HAS_PREMIUM_PLAN
            message = t('manage:space_transfer_error_premium_space');
            break;
        }
      }
      return dialog.alert({
        title: message
      });
    }
  };
  return <div className={S.container} data-sentry-component="SpaceTransferPageView" data-sentry-source-file="space-transfer.tsx">
      <span className={S.title}>{t('manage:space_transfer_title')}</span>
      <div className={S.form}>
        <TextField label={t('manage:space_transfer_target')} description={t('manage:space_transfer_target_description')} error={!!errors.targetPlayerEmail} helperText={errors.targetPlayerEmail?.message} data-sentry-element="TextField" data-sentry-source-file="space-transfer.tsx">
          <Input {...register('targetPlayerEmail')} type="email" placeholder={t('manage:space_transfer_target_placeholder')} isFullWidth={true} data-sentry-element="Input" data-sentry-source-file="space-transfer.tsx" />
        </TextField>
        <TextField label={t('manage:space_transfer_space_url')} error={!!errors.spaceUri} helperText={errors.spaceUri?.message} data-sentry-element="TextField" data-sentry-source-file="space-transfer.tsx">
          <Input {...register('spaceUri')} type="url" placeholder={t('manage:space_transfer_space_url_placeholder')} isFullWidth={true} data-sentry-element="Input" data-sentry-source-file="space-transfer.tsx" />
        </TextField>
        <div className={S.notice}>
          <div className={S.notice_header}>
            <ExclamationmarkSmallTriangleFillIcon width={16} height={16} data-sentry-element="ExclamationmarkSmallTriangleFillIcon" data-sentry-source-file="space-transfer.tsx" />
            <span className={S.notice_title}>
              {t('manage:space_transfer_notice_title')}
            </span>
          </div>
          <div className={S.notice_contents}>
            <Trans t={t} i18nKey={'manage:space_transfer_notice'} components={{
            Terms: <a href={getChannelTalkSupportArticleUrl(locale, 'b40ac18f')} target="_blank" />
          }} data-sentry-element="Trans" data-sentry-source-file="space-transfer.tsx" />
          </div>
        </div>
        <Checkbox {...register('isChecked')} label={t('manage:space_transfer_checked')} isFullWidth={true} data-sentry-element="Checkbox" data-sentry-source-file="space-transfer.tsx" />
        <Button disabled={!isChecked} onClick={handleSubmit(onSubmit)} data-sentry-element="Button" data-sentry-source-file="space-transfer.tsx">
          {t('manage:space_transfer_title')}
        </Button>
      </div>
    </div>;
};
const SpaceTransferSchema = (t: TFunction<['manage']>) => z.object({
  targetPlayerEmail: z.string().regex(emailRegex, t('manage:space_transfer_invalid_email')),
  spaceUri: z.string().url(t('manage:space_transfer_invalid_url')).includes('zep.us/play', {
    message: t('manage:space_transfer_invalid_url')
  }),
  isChecked: z.boolean()
});
type SpaceTransferSchemaType = z.infer<ReturnType<typeof SpaceTransferSchema>>;